import { FC, useMemo } from "react";
import { SidebarHeader, SidebarState, useSidebar } from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { useContent } from "@/hooks/useContent";
import { useNavigate } from "react-router-dom";
import AppRoutes from "@/constants/routes/AppRoutes";
import useSettingsStore from "@/stores/settings.store";
import LaraIcon from "@/assets/lara/lara-icon.svg";
import { Domain, getDomain } from "@/utils/domain";

const AppSidebarHeader: FC = () => {
  const { t } = useContent();
  const navigate = useNavigate();
  const { isDarkMode } = useSettingsStore();

  const handleHomeClick = () => {
    navigate(AppRoutes.dashboard);
  };
  const { state } = useSidebar();

  const isLoda = useMemo(
    () => getDomain(window.location.hostname) === Domain.LODA,
    []
  );

  const { logoSrc, showLabel, isTranslationLogo } = useMemo(() => {
    // Get translation-based logo (customers may have dedicated logo assets)
    const translationLogo =
      state === "expanded"
        ? isDarkMode
          ? t("sidebar.open.logo.dark")
          : t("sidebar.open.logo.light")
        : isDarkMode
          ? t("sidebar.collapsed.logo.dark")
          : t("sidebar.collapsed.logo.light");

    // Check if translation contains a valid logo (not the key itself, not empty)
    // Note: ContentProvider's t() prepends "translation." so we check both variants
    const translationKey =
      state === "expanded"
        ? isDarkMode
          ? "sidebar.open.logo.dark"
          : "sidebar.open.logo.light"
        : isDarkMode
          ? "sidebar.collapsed.logo.dark"
          : "sidebar.collapsed.logo.light";

    const hasValidTranslationLogo =
      Boolean(translationLogo) &&
      translationLogo !== translationKey &&
      translationLogo !== `translation.${translationKey}` &&
      !translationLogo.startsWith("translation.") &&
      translationLogo.trim() !== "";

    // If customer has a dedicated logo, use it (for both Loda and Lara)
    if (hasValidTranslationLogo) {
      return {
        logoSrc: translationLogo,
        showLabel: false,
        isTranslationLogo: true,
      };
    }

    // Fallback: Loda still uses translation (even if invalid), Lara uses default icon
    if (isLoda) {
      return {
        logoSrc: translationLogo,
        showLabel: false,
        isTranslationLogo: true,
      };
    }

    // Lara fallback: use the default icon; when expanded, show "Lara.law" next to it.
    return {
      logoSrc: LaraIcon,
      showLabel: state === "expanded",
      isTranslationLogo: false,
    };
  }, [isLoda, isDarkMode, state, t]);

  return (
    <SidebarHeader>
      <button
        onClick={handleHomeClick}
        data-testid="menu-home-button"
        className={cn(
          "cursor-pointer flex items-center gap-2",
          state === SidebarState.COLLAPSED && "w-full justify-center"
        )}
      >
        <img
          src={logoSrc}
          className={isTranslationLogo ? "max-h-8" : "h-8 w-8 shrink-0"}
          alt="Logo"
        />
        {showLabel && (
          <span className="text-sm font-semibold tracking-tight text-sidebar-foreground">
            Lara.law
          </span>
        )}
      </button>
    </SidebarHeader>
  );
};

export default AppSidebarHeader;
